import {
	podcastShowsList, podcastShowByID, newPodcastShow, updatePodcastShow, deletePodcastShow,
	podcastEpisodesList, podcastEpisodeByID, newPodcastEpisode, updatePodcastEpisode, deletePodcastEpisode, transitionPodcastEpisode, podcastEpisodeInsights,
	podcastGuestsList, podcastGuestByID, newPodcastGuest, updatePodcastGuest, deletePodcastGuest, invitePodcastGuest, updatePodcastGuestStatus, sendPodcastGuestConsent, sendPodcastGuestThankYou, uploadPodcastGuestPhotos, deletePodcastGuestPhoto,
	podcastEpisodesCalendar,
	podcastHostsList, podcastHostByID, newPodcastHost, updatePodcastHost, deletePodcastHost,
	podcastSponsorsList, podcastSponsorByID, newPodcastSponsor, updatePodcastSponsor, deletePodcastSponsor,
	digitalConsentsList, digitalConsentByID, revokeDigitalConsent,
} from '@/services/podcasts.service'

const emptyPagination = { meta: { pagination: { total: 0 } } }

export default {
	namespaced: true,
	state: {
		showsList: { ...emptyPagination },
		episodesList: { ...emptyPagination },
		guestsList: { ...emptyPagination },
		hostsList: { ...emptyPagination },
		sponsorsList: { ...emptyPagination },
		consentsList: { ...emptyPagination },
		currentItem: {},
		status: 'init',
		saveStatus: 'init',
		deleteStatus: 'init',
	},
	getters: {
		getShowsList: s => s.showsList,
		getEpisodesList: s => s.episodesList,
		getGuestsList: s => s.guestsList,
		getHostsList: s => s.hostsList,
		getSponsorsList: s => s.sponsorsList,
		getConsentsList: s => s.consentsList,
		getCurrentItem: s => s.currentItem,
		getStatus: s => s.status,
		getSaveStatus: s => s.saveStatus,
		getDeleteStatus: s => s.deleteStatus,
	},
	mutations: {
		SET_LIST(state, { key, value }) { state[key] = value },
		SET_CURRENT_ITEM(state, value) { state.currentItem = value },
		SET_STATUS(state, value) { state.status = value },
		SET_SAVE_STATUS(state, value) { state.saveStatus = value },
		SET_DELETE_STATUS(state, value) { state.deleteStatus = value },
	},
	actions: {
		// Generic fetch helper
		async fetchList({ commit }, { serviceFn, listKey, params }) {
			window.scrollTo({ top: 0, behavior: 'smooth' })
			commit('SET_STATUS', 'pending')
			commit('SET_LIST', { key: listKey, value: { ...emptyPagination } })
			try {
				const { data } = await serviceFn(params)
				commit('SET_LIST', { key: listKey, value: data })
				commit('SET_STATUS', 'init')
				return data
			} catch (e) { commit('SET_STATUS', 'init'); throw e }
		},
		async fetchById({ commit }, { serviceFn, id }) {
			commit('SET_STATUS', 'pending')
			try {
				const { data } = await serviceFn(id)
				commit('SET_CURRENT_ITEM', data.data)
				commit('SET_STATUS', 'init')
				return data
			} catch (e) { commit('SET_STATUS', 'init'); throw e }
		},
		async saveItem({ commit }, { createFn, updateFn, params, editing }) {
			commit('SET_SAVE_STATUS', 'pending')
			try {
				const { data } = editing ? await updateFn(params) : await createFn(params)
				commit('SET_SAVE_STATUS', 'init')
				return data
			} catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},
		async deleteItem({ commit }, { serviceFn, id }) {
			commit('SET_DELETE_STATUS', 'pending')
			try {
				const { data } = await serviceFn(id)
				commit('SET_DELETE_STATUS', 'init')
				return data
			} catch (e) { commit('SET_DELETE_STATUS', 'error'); throw e }
		},

		// Shows
		fetchShows({ dispatch }, params) { return dispatch('fetchList', { serviceFn: podcastShowsList, listKey: 'showsList', params }) },
		fetchShow({ dispatch }, id) { return dispatch('fetchById', { serviceFn: podcastShowByID, id }) },
		saveShow({ dispatch }, { params, editing }) { return dispatch('saveItem', { createFn: newPodcastShow, updateFn: updatePodcastShow, params, editing }) },
		deleteShow({ dispatch }, id) { return dispatch('deleteItem', { serviceFn: deletePodcastShow, id }) },

		// Episodes
		fetchEpisodes({ dispatch }, params) { return dispatch('fetchList', { serviceFn: podcastEpisodesList, listKey: 'episodesList', params }) },
		fetchEpisode({ dispatch }, id) { return dispatch('fetchById', { serviceFn: podcastEpisodeByID, id }) },
		saveEpisode({ dispatch }, { params, editing }) { return dispatch('saveItem', { createFn: newPodcastEpisode, updateFn: updatePodcastEpisode, params, editing }) },
		deleteEpisode({ dispatch }, id) { return dispatch('deleteItem', { serviceFn: deletePodcastEpisode, id }) },
		async fetchEpisodeInsights(_, params) {
			const { data } = await podcastEpisodeInsights(params)
			return data?.data || { gaps: {}, seasons: [] }
		},
		async transitionEpisode({ commit }, { id, status }) {
			commit('SET_SAVE_STATUS', 'pending')
			try { const { data } = await transitionPodcastEpisode(id, status); commit('SET_SAVE_STATUS', 'init'); return data } catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},

		// Guests
		fetchGuests({ dispatch }, params) { return dispatch('fetchList', { serviceFn: podcastGuestsList, listKey: 'guestsList', params }) },
		fetchGuest({ dispatch }, id) { return dispatch('fetchById', { serviceFn: podcastGuestByID, id }) },
		saveGuest({ dispatch }, { params, editing }) { return dispatch('saveItem', { createFn: newPodcastGuest, updateFn: updatePodcastGuest, params, editing }) },
		deleteGuest({ dispatch }, id) { return dispatch('deleteItem', { serviceFn: deletePodcastGuest, id }) },
		async inviteGuest({ commit }, { id, locale }) {
			commit('SET_SAVE_STATUS', 'pending')
			try { const { data } = await invitePodcastGuest(id, locale); commit('SET_SAVE_STATUS', 'init'); return data } catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},
		async updateGuestStatus({ commit }, { id, status }) {
			commit('SET_SAVE_STATUS', 'pending')
			try { const { data } = await updatePodcastGuestStatus(id, status); commit('SET_SAVE_STATUS', 'init'); return data } catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},
		async sendGuestConsent({ commit }, { id, locale }) {
			commit('SET_SAVE_STATUS', 'pending')
			try { const { data } = await sendPodcastGuestConsent(id, locale); commit('SET_SAVE_STATUS', 'init'); return data } catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},
		async sendGuestThankYou({ commit }, { id }) {
			commit('SET_SAVE_STATUS', 'pending')
			try { const { data } = await sendPodcastGuestThankYou(id); commit('SET_SAVE_STATUS', 'init'); return data } catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},
		async uploadGuestPhotos({ commit }, { id, formData }) {
			commit('SET_SAVE_STATUS', 'pending')
			try { const { data } = await uploadPodcastGuestPhotos(id, formData); commit('SET_SAVE_STATUS', 'init'); return data } catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},
		async deleteGuestPhoto({ commit }, { id, photoUrl }) {
			commit('SET_SAVE_STATUS', 'pending')
			try { const { data } = await deletePodcastGuestPhoto(id, photoUrl); commit('SET_SAVE_STATUS', 'init'); return data } catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},
		async fetchCalendar({ commit }, params) {
			commit('SET_STATUS', 'pending')
			try { const { data } = await podcastEpisodesCalendar(params); commit('SET_STATUS', 'init'); return data } catch (e) { commit('SET_STATUS', 'init'); throw e }
		},

		// Hosts
		fetchHosts({ dispatch }, params) { return dispatch('fetchList', { serviceFn: podcastHostsList, listKey: 'hostsList', params }) },
		fetchHost({ dispatch }, id) { return dispatch('fetchById', { serviceFn: podcastHostByID, id }) },
		saveHost({ dispatch }, { params, editing }) { return dispatch('saveItem', { createFn: newPodcastHost, updateFn: updatePodcastHost, params, editing }) },
		deleteHost({ dispatch }, id) { return dispatch('deleteItem', { serviceFn: deletePodcastHost, id }) },

		// Sponsors
		fetchSponsors({ dispatch }, params) { return dispatch('fetchList', { serviceFn: podcastSponsorsList, listKey: 'sponsorsList', params }) },
		fetchSponsor({ dispatch }, id) { return dispatch('fetchById', { serviceFn: podcastSponsorByID, id }) },
		saveSponsor({ dispatch }, { params, editing }) { return dispatch('saveItem', { createFn: newPodcastSponsor, updateFn: updatePodcastSponsor, params, editing }) },
		deleteSponsor({ dispatch }, id) { return dispatch('deleteItem', { serviceFn: deletePodcastSponsor, id }) },

		// Consents
		fetchConsents({ dispatch }, params) { return dispatch('fetchList', { serviceFn: digitalConsentsList, listKey: 'consentsList', params }) },
		fetchConsent({ dispatch }, id) { return dispatch('fetchById', { serviceFn: digitalConsentByID, id }) },
		async revokeConsent({ commit }, { id, reason }) {
			commit('SET_SAVE_STATUS', 'pending')
			try { const { data } = await revokeDigitalConsent(id, reason); commit('SET_SAVE_STATUS', 'init'); return data } catch (e) { commit('SET_SAVE_STATUS', 'init'); throw e }
		},
	},
}
